import type { SceneGroup } from '@lumen5/luminary';

import { getVoiceDrivenAssetDurationAfterCutsApplied } from 'js/src/luminary/luminary-selectors';
import voicedrivenSceneGroupDurationFixerSchedule from 'js/src/luminary/voicedriven-scenegroup-duration-fixer-schedule';
import {
    getVoicedrivenSceneDuration,
    runVoicedrivenBatchRun,
    setVoicedrivenSceneDuration,
} from 'js/src/luminary/voicedriven-scenes';

/**
 * Synchronous variant of {@link updateSceneGroupDurationToEqualVoiceDrivenAssetDuration}: mutates
 * the last scene's logical span so the scene group's total raw duration matches the voicedriven
 * asset's duration-after-cuts.
 *
 * MUST be called from inside a {@link runVoicedrivenBatchRun} — uses
 * {@link setVoicedrivenSceneDuration} so the end-of-batch reconcile adds the transition back via
 * `Scene.calculateDurationForLogicalSpan`, keeping snapshot bookkeeping consistent.
 *
 * Use this when you're already inside a voicedriven batch (e.g. combining this fix with an
 * `uncut()` call) and have already awaited the asset load. Otherwise prefer the async wrapper.
 */
export const synchronouslyUpdateSceneGroupDurationToEqualVoiceDrivenAssetDuration = (
    sceneGroup: SceneGroup,
) => {
    const voiceDrivenAssetDuration = getVoiceDrivenAssetDurationAfterCutsApplied(sceneGroup);
    if (voiceDrivenAssetDuration === 0) {
        // things are obviously not set up yet.
        return;
    }
    const lastScene = sceneGroup.scenes.at(-1);
    if (!lastScene) {
        throw new Error('No last scene found');
    }
    // Bump the last scene's logical span by the raw-sum delta. For scenes whose transition isn't
    // being capped by scaleFactor, delta(raw) == delta(logical) and the end-of-batch reconcile
    // produces a raw value that closes the gap exactly.
    const durationDiff = voiceDrivenAssetDuration - sceneGroup.duration;
    setVoicedrivenSceneDuration(lastScene, getVoicedrivenSceneDuration(lastScene) + durationDiff);
};

/**
 * The applied source data duration is usually a rounded off value
 * and we only get the 'real' duration of the asset after it has loaded.
 *
 * When this happens, we want to make sure that the scene duration matches
 * the loaded asset duration, so we add the diff to the last scene.
 *
 * If we don't do this, we end up with a scene group longer than the media
 * which results in the base scene design showing from the end time
 * of the last transcript word till the end of the scene group.
 *
 * The adjustment is routed through {@link runVoicedrivenBatchRun} so the end-of-batch reconcile
 * adds transitions back via `Scene.calculateDurationForLogicalSpan`. This keeps the snapshot
 * bookkeeping consistent with any other voicedriven batch that might be in flight.
 *
 * Callers that are already inside a voicedriven batch and want to combine this fix with other
 * synchronous work should call {@link synchronouslyUpdateSceneGroupDurationToEqualVoiceDrivenAssetDuration}
 * directly.
 */
export const updateSceneGroupDurationToEqualVoiceDrivenAssetDuration = async ({
    sceneGroup,
    waitForLuminary = true,
}: {
    sceneGroup: SceneGroup;
    waitForLuminary?: boolean;
}) => {
    voicedrivenSceneGroupDurationFixerSchedule.increment(sceneGroup.id);
    try {
        if (waitForLuminary) {
            await sceneGroup.root.onCanPlay();
        }
        if (sceneGroup.destroyed) {
            return; // fine, the scene was destroyed before the asset loaded
        }

        runVoicedrivenBatchRun(sceneGroup.root, sceneGroup.id, () => {
            synchronouslyUpdateSceneGroupDurationToEqualVoiceDrivenAssetDuration(sceneGroup);
        });
    } finally {
        voicedrivenSceneGroupDurationFixerSchedule.decrement(sceneGroup.id);
    }
};
